import { useMemo, useState } from "react";
import styles from "./StatisticsBar.module.scss";
import {
  CreditCard02Icon,
  Download01Icon,
  MessageAlertSquareIcon,
  PlusIcon,
  Rss01Icon,
  Users01Icon,
} from "@vetty/icons";
import { Button } from "@/components";
import type { StatisticsOverviewData } from "../../_types/statistics-api.types";
import {
  formatPercentDelta,
  formatStatInt,
} from "../../_utils/statisticsFormat.util";
import { StatisticsBarCard } from "..";
import {
  StatisticsDateRangePicker,
  type StatisticsDateRangePickerChange,
} from "../statistics-date-range-picker";
import { StatisticsExportDrawer } from "../statistics-export-drawer";
import { StatisticsReportDrawer } from "../statistics-report-drawer";
import { buildStatisticsReportModel } from "../../_utils/statisticsReportModel.util";

type StatisticsBarProps = {
  onBoundsChange: (value: StatisticsDateRangePickerChange) => void;
  overview: StatisticsOverviewData | null;
  prevOverview: StatisticsOverviewData | null;
  loading: boolean;
  error: string | null;
};

export const StatisticsBar = ({
  onBoundsChange,
  overview,
  prevOverview,
  loading,
  error,
}: StatisticsBarProps) => {
  const [reportDrawerOpen, setReportDrawerOpen] = useState(false);
  const [exportDrawerOpen, setExportDrawerOpen] = useState(false);

  const reportModel = useMemo(
    () => buildStatisticsReportModel(overview, prevOverview, loading),
    [overview, prevOverview, loading],
  );

  const cur = overview?.range;
  const prev = prevOverview?.range;

  const activeDelta =
    cur && prev
      ? formatPercentDelta(
          cur.activeUsersInRange,
          prev.activeUsersInRange,
        )
      : null;
  const totalDelta =
    cur && prev
      ? formatPercentDelta(cur.totalUsersAtEnd, prev.totalUsersAtEnd)
      : null;
  const membersDelta =
    cur && prev
      ? formatPercentDelta(cur.memberUsersAtEnd, prev.memberUsersAtEnd)
      : null;
  const messagesDelta =
    cur && prev
      ? formatPercentDelta(
          cur.formsSubmittedInRange,
          prev.formsSubmittedInRange,
        )
      : null;

  const dash = "—";

  const canCreateReport = reportModel != null;

  return (
    <div className={styles["comp"]}>
      <div className={styles["comp__header"]}>
        <div className={styles["comp__header__filters"]}>
          <StatisticsDateRangePicker onChange={onBoundsChange} />
        </div>

        <div className={styles["comp__header__actions"]}>
          <Button
            type="button"
            startContent={<Download01Icon style={{ width: 20, height: 20 }} />}
            variant="secondary"
            disabled={loading || !overview}
            onClick={() => setExportDrawerOpen(true)}
          >
            Export data
          </Button>
          <Button
            type="button"
            startContent={<PlusIcon style={{ width: 20, height: 20 }} />}
            disabled={!canCreateReport}
            onClick={() => setReportDrawerOpen(true)}
          >
            Create report
          </Button>
        </div>
      </div>

      <StatisticsExportDrawer
        isOpen={exportDrawerOpen}
        onClose={() => setExportDrawerOpen(false)}
        overview={overview}
        loading={loading}
      />

      <StatisticsReportDrawer
        isOpen={reportDrawerOpen}
        onClose={() => setReportDrawerOpen(false)}
        model={reportModel}
      />

      {error ? (
        <p className={styles["comp__error"]} role="alert">
          {error}
        </p>
      ) : null}

      <div className={styles["comp__content"]}>
        <StatisticsBarCard
          id="active-users"
          icon={<Rss01Icon style={{ width: 20, height: 20 }} />}
          heading="Active users"
          value={
            loading || !cur ? dash : formatStatInt(cur.activeUsersInRange)
          }
          percentage={activeDelta?.label ?? dash}
          variant={activeDelta?.variant ?? "none"}
        />
        <StatisticsBarCard
          id="total-accounts"
          icon={<Users01Icon style={{ width: 20, height: 20 }} />}
          heading="Total accounts"
          value={loading || !cur ? dash : formatStatInt(cur.totalUsersAtEnd)}
          percentage={totalDelta?.label ?? dash}
          variant={totalDelta?.variant ?? "none"}
        />
        <StatisticsBarCard
          id="members-joined"
          icon={<CreditCard02Icon style={{ width: 20, height: 20 }} />}
          heading="Members"
          value={
            loading || !cur ? dash : formatStatInt(cur.memberUsersAtEnd)
          }
          percentage={membersDelta?.label ?? dash}
          variant={membersDelta?.variant ?? "none"}
        />

        <StatisticsBarCard
          id="messages"
          icon={<MessageAlertSquareIcon style={{ width: 20, height: 20 }} />}
          heading="Messages"
          value={
            loading || !cur ? dash : formatStatInt(cur.formsSubmittedInRange)
          }
          percentage={messagesDelta?.label ?? dash}
          variant={messagesDelta?.variant ?? "none"}
        />
      </div>
    </div>
  );
};
